import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CreateUserRequest,
  UpdateUserRequest,
  UserListItem,
} from '@produzionejb/types';
import { api } from './api-client';

const QK = {
  list: ['users', 'list'] as const,
};

export interface UsersListResponse {
  items: UserListItem[];
  smtpConfigured: boolean;
}

export interface InviteCreatedResponse {
  user: UserListItem;
  inviteExpiresAt: string;
  inviteUrl?: string;
}

export interface ResendInviteResponse {
  inviteExpiresAt: string;
  inviteUrl?: string;
}

export function useUsers() {
  return useQuery({
    queryKey: QK.list,
    queryFn: () => api<UsersListResponse>('/api/users'),
  });
}

export function useInviteUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateUserRequest) =>
      api<InviteCreatedResponse>('/api/users', { method: 'POST', body }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: QK.list });
    },
  });
}

export function useUpdateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdateUserRequest }) =>
      api<UserListItem>(`/api/users/${id}`, { method: 'PATCH', body }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: QK.list });
    },
  });
}

export function useResendInvite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api<ResendInviteResponse>(`/api/users/${id}/resend-invite`, { method: 'POST' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: QK.list });
    },
  });
}

export function useUnlockUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api<{ status: 'ok' }>(`/api/users/${id}/unlock`, { method: 'POST' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: QK.list });
    },
  });
}

export function useDeleteUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api<void>(`/api/users/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: QK.list });
    },
  });
}

export interface InvitePeekResponse {
  email: string;
  name: string | null;
  expiresAt: string;
}

export function fetchInvitePeek(token: string): Promise<InvitePeekResponse> {
  return api<InvitePeekResponse>(`/api/auth/invite/${encodeURIComponent(token)}`, { auth: false });
}

export interface AcceptInviteResponse {
  status: 'ok';
  accessToken: string;
  user: { id: string; email: string; role: 'ADMIN' | 'MANAGER' | 'OPERATOR'; mfaEnabled: boolean };
}

export function acceptInvite(token: string, password: string): Promise<AcceptInviteResponse> {
  return api<AcceptInviteResponse>('/api/auth/accept-invite', {
    method: 'POST',
    auth: false,
    body: { token, password },
  });
}
